// GameCanvas.jsx
import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import {
  Stage,
  Graphics,
  Text,
  Sprite,
  Container,
} from "@pixi/react";
import BorderedSprite from "./BorderedSprite";
import { settings } from "@pixi/settings";
import { SCALE_MODES } from "@pixi/constants";
import MapTiles from "./MapTiles";
import { TerritoryLayer } from "./TerritoryRenderer";
import RegionOverlay from "./RegionOverlay";

// Extracted modules
import { usePanZoom } from "../hooks/usePanZoom";
import {
  resourceIconMap,
  buildIconMap,
  CAPTURE_EFFECT_DURATION_MS,
  CAPTURE_EFFECT_MAX,
  TOUCH_LONG_PRESS_MS,
  TOUCH_LONG_PRESS_MOVE_PX,
  COMBAT_FLASH_DURATION,
} from "./canvas/constants";
import {
  renderArrowPath,
  renderArrowV2,
  simplifyArrowPath,
  ArrowTroopFlow,
  sameOwner,
  cloneAttackArrow,
  getAttackArrowKey,
  interpolateAttackArrow,
} from "./canvas/ArrowRenderer";
import {
  TroopDensityHeatmap,
  BorderPressureOverlay,
  CombatFlashLayer,
} from "./canvas/EffectsLayer";
import {
  renderResources,
  renderResourceCaptureOverlays,
} from "./canvas/ResourceRenderer";
import {
  NationOverlay,
} from "./canvas/NationOverlay";

// Feature flag for optimized territory rendering (set via environment or default false)
const USE_OPTIMIZED_TERRITORY = process.env.REACT_APP_OPTIMIZED_TERRITORY === "true";

settings.SCALE_MODE = SCALE_MODES.NEAREST;

// Mirrors the server's arrow cost rules (arrowRoutes.js): base + per-tile,
// first arrow free, and the own-territory discount when the path's start AND
// end cells are both owned by the player.
function computeArrowCostInfo(path, arrowCostCfg, pNation, ownershipMap, userId) {
  if (!arrowCostCfg || !path || path.length < 2) {
    return { foodCost: 0, goldCost: 0, affordable: true };
  }
  let pathLen = 0;
  for (let i = 1; i < path.length; i++) {
    pathLen += Math.hypot(path[i].x - path[i - 1].x, path[i].y - path[i - 1].y);
  }
  let foodCost = arrowCostCfg.food.base + arrowCostCfg.food.perTile * pathLen;
  let goldCost = arrowCostCfg.gold.base + arrowCostCfg.gold.perTile * pathLen;
  const activeAttacks = pNation?.arrowOrders?.attacks?.length || 0;
  if (arrowCostCfg.firstArrowFree && activeAttacks === 0) {
    foodCost = 0;
    goldCost = 0;
  }
  if (arrowCostCfg.ownTerritoryDiscount && foodCost > 0 && ownershipMap) {
    const first = path[0];
    const last = path[path.length - 1];
    const startIn = sameOwner(ownershipMap.get((first.y << 16) | first.x), userId);
    const endIn = sameOwner(ownershipMap.get((last.y << 16) | last.x), userId);
    if (startIn && endIn) {
      foodCost *= arrowCostCfg.ownTerritoryDiscount;
      goldCost *= arrowCostCfg.ownTerritoryDiscount;
    }
  }
  foodCost = Math.ceil(foodCost);
  goldCost = Math.ceil(goldCost);
  const affordable =
    (pNation?.resources?.food || 0) >= foodCost &&
    (pNation?.resources?.gold || 0) >= goldCost;
  return { foodCost, goldCost, affordable };
}

/* -------------------------------------------------------------------------- */
/*                              Main Component                              */
/* -------------------------------------------------------------------------- */

const GameCanvas = ({
  mapMetadata,
  mapGrid,
  mappings,
  gameState,
  userId,
  nationColors,
  config,
  foundingNation,
  onFoundNation,
  buildingStructure,
  onBuildCity,
  onCancelBuild,
  drawingArrowType,
  onStartDrawArrow,
  currentArrowPath,
  onArrowPathUpdate,
  onSendArrow,
  onCancelArrow,
  activeAttackArrows,
  activeDefendArrow,
  uiMode,
  isMobile,
  onInspectCell,
  onError,
  troopDensityMap,
  combatFlashes,
  setCombatFlashes,
  regionData,
  isDiscord,
  stageWidth,
  stageHeight,
}) => {
  const viewportWidth = Math.max(
    1,
    Math.floor(stageWidth || window.innerWidth || 1)
  );
  const viewportHeight = Math.max(
    1,
    Math.floor(stageHeight || window.innerHeight || 1)
  );

  const {
    cellSize,
    scale,
    offset,
    setOffsetClamped,
    panBy,
    handleWheel,
    zoomAtPoint,
    getCellCoordinates,
  } = usePanZoom({
    mapMetadata,
    stageWidth: viewportWidth,
    stageHeight: viewportHeight,
  });

  // Local state for unit selections and hovered cell
  const [hoveredCell, setHoveredCell] = useState(null);
  const hoverFrameRef = useRef(null);
  const pendingHoverRef = useRef(null);
  const nations = gameState?.gameState?.nations || [];
  const playerNation = useMemo(() =>
    nations.find((n) => sameOwner(n.owner, userId) && n.status !== "defeated") || null,
    [nations, userId]
  );
  const resourceNodeClaims = gameState?.gameState?.resourceNodeClaims || {};
  const captureTicks =
    config?.territorial?.resourceCaptureTicks || 20;
  const [captureEffects, setCaptureEffects] = useState([]);
  const [captureEffectTime, setCaptureEffectTime] = useState(0);
  const captureInitRef = useRef(false);
  const lastClaimOwnersRef = useRef({});
  const [attackInterpAlpha, setAttackInterpAlpha] = useState(1);
  const attackInterpRef = useRef({
    from: [],
    to: (activeAttackArrows || []).map(cloneAttackArrow),
    startMs: performance.now(),
    durationMs: Math.max(120, config?.territorial?.tickRateMs || 200),
  });
  const ownershipMap = useMemo(() => {
    const map = new Map();
    nations.forEach((nation) => {
      const tx = nation?.territory?.x || [];
      const ty = nation?.territory?.y || [];
      for (let i = 0; i < tx.length; i++) {
        map.set((ty[i] << 16) | tx[i], nation.owner);
      }
    });
    return map;
  }, [nations]);

  useEffect(() => {
    if (!resourceNodeClaims) return;
    const currentOwners = {};
    const newEffects = [];
    Object.entries(resourceNodeClaims).forEach(([key, claim]) => {
      const owner = claim?.owner || null;
      currentOwners[key] = owner;
      if (!owner || !captureInitRef.current) return;
      const prevOwner = lastClaimOwnersRef.current?.[key] || null;
      if (owner === prevOwner) return;
      if (userId && !sameOwner(owner, userId)) return;
      const [xStr, yStr] = key.split(",");
      const x = Number(xStr);
      const y = Number(yStr);
      if (!Number.isFinite(x) || !Number.isFinite(y)) return;
      const resourceName = String(claim?.type || "resource").replace(/_/g, " ");
      newEffects.push({
        id: `${key}-${owner}-${Date.now()}`,
        x,
        y,
        text: `${resourceName} node captured`,
        createdAt: performance.now(),
      });
    });
    if (!captureInitRef.current) {
      captureInitRef.current = true;
      lastClaimOwnersRef.current = currentOwners;
      return;
    }
    lastClaimOwnersRef.current = currentOwners;
    if (newEffects.length) {
      setCaptureEffects((prev) => {
        if (prev.length === 0) return newEffects.slice(-CAPTURE_EFFECT_MAX);
        const combined = prev.concat(newEffects);
        return combined.length <= CAPTURE_EFFECT_MAX ? combined : combined.slice(-CAPTURE_EFFECT_MAX);
      });
    }
  }, [resourceNodeClaims, userId]);

  useEffect(() => {
    const nextTo = (activeAttackArrows || []).map(cloneAttackArrow);
    attackInterpRef.current = {
      from: attackInterpRef.current.to,
      to: nextTo,
      startMs: performance.now(),
      durationMs: Math.max(120, config?.territorial?.tickRateMs || 200),
    };
    setAttackInterpAlpha(0);
  }, [activeAttackArrows, config?.territorial?.tickRateMs]);

  useEffect(() => {
    let rafId;
    const animate = () => {
      const interp = attackInterpRef.current;
      const elapsed = performance.now() - interp.startMs;
      const nextAlpha = Math.min(1, elapsed / Math.max(1, interp.durationMs));
      setAttackInterpAlpha((prev) => {
        if (Math.abs(prev - nextAlpha) < 0.01) return prev;
        return nextAlpha;
      });
      if (nextAlpha < 1) {
        rafId = requestAnimationFrame(animate);
      }
    };

    rafId = requestAnimationFrame(animate);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [activeAttackArrows, config?.territorial?.tickRateMs]);

  const smoothedAttackArrows = useMemo(() => {
    const { from, to } = attackInterpRef.current;
    if (!to.length || attackInterpAlpha >= 0.999) return to;

    const fromByKey = new Map();
    for (let i = 0; i < from.length; i++) {
      fromByKey.set(getAttackArrowKey(from[i], i), from[i]);
    }

    return to.map((arrow, idx) => {
      const key = getAttackArrowKey(arrow, idx);
      const previous = fromByKey.get(key);
      return interpolateAttackArrow(previous, arrow, attackInterpAlpha);
    });
  }, [attackInterpAlpha]);

  const buildCellInfo = useCallback(
    (cell, x, y) => {
      if (!cell) return null;
      const biomeName =
        mappings?.biomes?.[cell[3]] || cell[3] || "UNKNOWN";
      const rawResources = Array.isArray(cell[5]) ? cell[5] : [];
      const resources = rawResources.map((r) =>
        typeof r === "string" ? r : mappings?.resources?.[r] ?? r
      );
      const owner = ownershipMap.get((y << 16) | x) || null;
      const ownerNation = owner
        ? nations.find((n) => n.owner === owner)
        : null;
      const structure = ownerNation?.cities?.find(
        (c) => c.x === x && c.y === y
      );
      const claim = resourceNodeClaims?.[`${x},${y}`] || null;
      return {
        x,
        y,
        biome: biomeName,
        resources,
        owner,
        ownerColor: owner ? nationColors?.[owner] : null,
        structure,
        claim,
      };
    },
    [mappings, ownershipMap, nations, resourceNodeClaims, nationColors]
  );

  const mapGridByRow = useMemo(() => {
    if (!mapMetadata) return [];
    const rows = Array.from({ length: mapMetadata.height }, () =>
      Array.from({ length: mapMetadata.width }, () => null)
    );
    for (let i = 0; i < mapGrid.length; i++) {
      const entry = mapGrid[i];
      if (!entry) continue;
      const { cell, x, y } = entry;
      if (y >= 0 && y < rows.length && x >= 0 && x < rows[y].length) {
        rows[y][x] = cell;
      }
    }
    return rows;
  }, [mapGrid, mapMetadata]);

  const biomeColors = useMemo(
    () => ({
      OCEAN: 0x1b4f72,
      COASTAL: 0x2e86c1,
      RIVER: 0x3498db,
      MOUNTAIN: 0x7f8c8d,
      DESERT: 0xd4ac0d,
      SAVANNA: 0xa9c46a,
      TROPICAL_FOREST: 0x1e8449,
      RAINFOREST: 0x145a32,
      TUNDRA: 0xd6eaf8,
      TAIGA: 0x4d9078,
      GRASSLAND: 0x6aa84f,
      WOODLAND: 0x3d7b3f,
      FOREST: 0x1b5e20,
      default: 0x6aa84f,
    }),
    []
  );

  const fullMapImageUrl = useMemo(() => {
    if (!mapMetadata || mapGridByRow.length === 0) return null;
    const width = mapMetadata.width;
    const height = mapMetadata.height;
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    const imageData = ctx.createImageData(width, height);
    const data = imageData.data;
    const biomeLookup = mappings?.biomes || {};
    for (let y = 0; y < height; y++) {
      const row = mapGridByRow[y];
      if (!row) continue;
      for (let x = 0; x < width; x++) {
        const cell = row[x];
        if (!cell) continue;
        const biomeName = biomeLookup[cell[3]] || "default";
        const color = biomeColors[biomeName] ?? biomeColors.default;
        const idx = (y * width + x) * 4;
        data[idx] = (color >> 16) & 0xff;
        data[idx + 1] = (color >> 8) & 0xff;
        data[idx + 2] = color & 0xff;
        data[idx + 3] = 255;
      }
    }
    ctx.putImageData(imageData, 0, 0);
    return canvas.toDataURL();
  }, [mapMetadata, mapGridByRow, mappings, biomeColors]);

  useEffect(() => {
    if (captureEffects.length === 0) return undefined;
    let animationFrameId;
    let lastUpdate = performance.now();
    const tick = (now) => {
      if (now - lastUpdate >= 50) {
        lastUpdate = now;
        setCaptureEffectTime(now);
      }
      animationFrameId = requestAnimationFrame(tick);
    };
    animationFrameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animationFrameId);
  }, [captureEffects.length]);

  useEffect(() => {
    if (captureEffects.length === 0) return;
    const now = captureEffectTime || performance.now();
    const cutoff = now - CAPTURE_EFFECT_DURATION_MS;
    const hasExpired = captureEffects.some((effect) => effect.createdAt < cutoff);
    if (!hasExpired) return;
    setCaptureEffects((prev) =>
      prev.filter((effect) => effect.createdAt >= cutoff)
    );
  }, [captureEffectTime, captureEffects]);

  // Prune expired combat flashes every second
  useEffect(() => {
    if (!combatFlashes || combatFlashes.length === 0) return;
    const interval = setInterval(() => {
      const now = performance.now();
      setCombatFlashes?.((prev) =>
        prev.filter((f) => now - f.createdAt < COMBAT_FLASH_DURATION)
      );
    }, 1000);
    return () => clearInterval(interval);
  }, [combatFlashes?.length, setCombatFlashes]);

  useEffect(() => {
    return () => {
      if (hoverFrameRef.current) {
        cancelAnimationFrame(hoverFrameRef.current);
      }
    };
  }, []);

  /* ----- Viewport State ----- */
  const [isPanning, setIsPanning] = useState(false);
  const drawTypeRef = useRef(null);
  const touchPointsRef = useRef(new Map());
  const longPressRef = useRef({
    armed: false,
    triggered: false,
    pointerId: null,
    startX: 0,
    startY: 0,
    timer: null,
  });
  const pinchRef = useRef({
    active: false,
    ids: [],
    startDistance: 1,
    startScale: 1,
    lastMid: null,
  });
  const panRef = useRef({
    active: false,
    startX: 0,
    startY: 0,
    startOffset: { x: 0, y: 0 },
    moved: false,
    button: null,
  });

  /* ----- Arrow Drawing State ----- */
  const isDrawingArrowRef = useRef(false);
  const arrowPathRef = useRef([]);
  const lastArrowPointRef = useRef(null);
  const configRef = useRef(config);
  configRef.current = config;
  const gameStateRef = useRef(gameState);
  gameStateRef.current = gameState;

  /* ----- Arrow Animation State ----- */

  /* ----- Smoother WASD Panning via Animation Frame ----- */
  const keysRef = useRef({ w: false, a: false, s: false, d: false });
  const panByRef = useRef(panBy);
  panByRef.current = panBy;
  // Latest mode state for the mount-once key handler (Escape cancel).
  const keyModeRef = useRef({});
  keyModeRef.current = {
    drawingArrowType,
    buildingStructure,
    onCancelArrow,
    onCancelBuild,
  };
  useEffect(() => {
    const isTypingTarget = (target) =>
      target &&
      (target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable);
    const handleKeyDown = (e) => {
      if (isTypingTarget(e.target)) return;
      if (e.key === "Escape") {
        const mode = keyModeRef.current;
        if (isDrawingArrowRef.current || mode.drawingArrowType) {
          isDrawingArrowRef.current = false;
          arrowPathRef.current = [];
          lastArrowPointRef.current = null;
          drawTypeRef.current = null;
          mode.onCancelArrow?.();
        } else if (mode.buildingStructure) {
          mode.onCancelBuild?.();
        }
        return;
      }
      const key = e.key.toLowerCase();
      if (["w", "a", "s", "d"].includes(key)) {
        keysRef.current[key] = true;
      }
    };
    const handleKeyUp = (e) => {
      if (isTypingTarget(e.target)) return;
      const key = e.key.toLowerCase();
      if (["w", "a", "s", "d"].includes(key)) {
        keysRef.current[key] = false;
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    let animationFrameId;
    let lastFrameTime = performance.now();
    const panSpeed = 0.5; // Adjust speed (pixels per ms)

    const update = () => {
      const now = performance.now();
      const deltaTime = now - lastFrameTime;
      lastFrameTime = now;
      let dx = 0,
        dy = 0;
      if (keysRef.current.w) dy += panSpeed * deltaTime;
      if (keysRef.current.s) dy -= panSpeed * deltaTime;
      if (keysRef.current.a) dx += panSpeed * deltaTime;
      if (keysRef.current.d) dx -= panSpeed * deltaTime;
      if (dx !== 0 || dy !== 0) {
        panByRef.current(dx, dy);
      }
      animationFrameId = requestAnimationFrame(update);
    };
    animationFrameId = requestAnimationFrame(update);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  /* ----- Pointer Handlers ----- */
  const clearLongPress = useCallback(() => {
    const lp = longPressRef.current;
    if (lp.timer) {
      clearTimeout(lp.timer);
    }
    lp.armed = false;
    lp.triggered = false;
    lp.pointerId = null;
    lp.timer = null;
  }, []);

  const beginPinchGesture = useCallback(
    (points) => {
      if (!points || points.size < 2) return false;
      const entries = Array.from(points.entries());
      const [idA, a] = entries[0];
      const [idB, b] = entries[1];
      if (!a || !b) return false;
      const mid = {
        x: (a.x + b.x) / 2,
        y: (a.y + b.y) / 2,
      };
      const distance = Math.max(1, Math.hypot(b.x - a.x, b.y - a.y));
      pinchRef.current = {
        active: true,
        ids: [idA, idB],
        startDistance: distance,
        startScale: scale,
        lastMid: mid,
      };
      setIsPanning(true);
      return true;
    },
    [scale]
  );

  const handlePointerDown = useCallback(
    (e) => {
      let x, y;
      if (e.data && e.data.global) {
        ({ x, y } = e.data.global);
      } else {
        const rect = e.target.getBoundingClientRect();
        x = e.nativeEvent.clientX - rect.left;
        y = e.nativeEvent.clientY - rect.top;
      }
      const originalEvent = e.data?.originalEvent || e.nativeEvent || {};
      const pointerId = originalEvent.pointerId ?? "mouse";
      const pointerType = originalEvent.pointerType || "mouse";
      const isTouchPointer = pointerType === "touch";
      if (isTouchPointer) {
        originalEvent.preventDefault?.();
      }

      const forcedDraw =
        uiMode === "drawAttack"
          ? "attack"
          : uiMode === "drawDefend"
          ? "defend"
          : drawingArrowType || "attack";
      // Pan mode is a true mode: no arrow drawing while it's active.
      const canDraw = !foundingNation && !buildingStructure && uiMode !== "pan";

      // Attack arrows are capped server-side; block the draw up front with
      // feedback instead of letting the submit fail invisibly.
      const canStartDraw = (drawType) => {
        if (drawType !== "attack") return true;
        const maxArrows = configRef.current?.territorial?.maxAttackArrows ?? 3;
        const nation = gameStateRef.current?.gameState?.nations?.find((n) =>
          sameOwner(n.owner, userId)
        );
        const activeAttacks = nation?.arrowOrders?.attacks?.length || 0;
        if (activeAttacks >= maxArrows) {
          onError?.(
            `All ${maxArrows} attack arrows are already active — cancel one or wait for it to finish.`
          );
          return false;
        }
        return true;
      };

      if (isTouchPointer) {
        touchPointsRef.current.set(pointerId, { x, y });
        if (touchPointsRef.current.size >= 2) {
          // Two-finger touch always switches to pan/zoom mode.
          clearLongPress();
          if (isDrawingArrowRef.current) {
            isDrawingArrowRef.current = false;
            arrowPathRef.current = [];
            lastArrowPointRef.current = null;
            drawTypeRef.current = null;
            onCancelArrow?.();
          }
          if (panRef.current.active) {
            panRef.current.active = false;
            panRef.current.moved = false;
            panRef.current.button = null;
          }
          beginPinchGesture(touchPointsRef.current);
          return;
        }

        // Single-finger touch drag pans by default.
        panRef.current.active = true;
        panRef.current.moved = false;
        panRef.current.startX = x;
        panRef.current.startY = y;
        panRef.current.startOffset = { ...offset };
        panRef.current.button = 0;
        setIsPanning(true);

        // Long-press converts this touch into arrow drawing.
        clearLongPress();
        if (canDraw) {
          const lp = longPressRef.current;
          lp.armed = true;
          lp.triggered = false;
          lp.pointerId = pointerId;
          lp.startX = x;
          lp.startY = y;
          lp.timer = setTimeout(() => {
            const current = longPressRef.current;
            if (
              !current.armed ||
              current.pointerId !== pointerId ||
              touchPointsRef.current.size !== 1
            ) {
              return;
            }
            const point = touchPointsRef.current.get(pointerId);
            if (!point) return;
            const moved = Math.hypot(
              point.x - current.startX,
              point.y - current.startY
            );
            if (moved > TOUCH_LONG_PRESS_MOVE_PX) return;

            current.triggered = true;
            if (panRef.current.active) {
              panRef.current.active = false;
              panRef.current.moved = false;
              panRef.current.button = null;
              setIsPanning(false);
            }

            const cell = getCellCoordinates(point.x, point.y);
            if (
              cell.x >= 0 &&
              cell.x < mapMetadata.width &&
              cell.y >= 0 &&
              cell.y < mapMetadata.height &&
              canStartDraw(forcedDraw)
            ) {
              drawTypeRef.current = forcedDraw;
              if (drawingArrowType !== forcedDraw) {
                onStartDrawArrow?.(forcedDraw);
              }
              isDrawingArrowRef.current = true;
              arrowPathRef.current = [{ x: cell.x, y: cell.y }];
              lastArrowPointRef.current = { x: cell.x, y: cell.y };
              onArrowPathUpdate?.([{ x: cell.x, y: cell.y }]);
            }
          }, TOUCH_LONG_PRESS_MS);
        }
        return;
      }
      // Check which button is pressed (0: left, 1: middle, 2: right)
      const button = originalEvent.button ?? e.nativeEvent?.button;
      const buttons =
        originalEvent.buttons ?? e.nativeEvent?.buttons ?? 0;
      const leftDown = (buttons & 1) === 1;
      const rightDown = (buttons & 2) === 2;
      const middleDown = (buttons & 4) === 4;
      const defendCombo = leftDown && rightDown;

      if (button === 2) {
        e.data?.originalEvent?.preventDefault?.();
        e.preventDefault?.();
      }
      const shouldPan =
        button === 1 ||
        button === 2 ||
        middleDown ||
        rightDown ||
        (uiMode === "pan" && button === 0);

      if (defendCombo && canDraw) {
        if (panRef.current.active) {
          panRef.current.active = false;
          panRef.current.moved = false;
          panRef.current.button = null;
          setIsPanning(false);
        }
        const cell = getCellCoordinates(x, y);
        if (
          cell.x >= 0 &&
          cell.x < mapMetadata.width &&
          cell.y >= 0 &&
          cell.y < mapMetadata.height
        ) {
          drawTypeRef.current = "defend";
          if (drawingArrowType !== "defend") {
            onStartDrawArrow?.("defend");
          }
          isDrawingArrowRef.current = true;
          arrowPathRef.current = [{ x: cell.x, y: cell.y }];
          lastArrowPointRef.current = { x: cell.x, y: cell.y };
          onArrowPathUpdate?.([{ x: cell.x, y: cell.y }]);
        }
        return;
      }

      if (shouldPan) {
        panRef.current.active = true;
        panRef.current.moved = false;
        panRef.current.startX = x;
        panRef.current.startY = y;
        panRef.current.startOffset = { ...offset };
        panRef.current.button = button;
        setIsPanning(true);
        return;
      }

      // Arrow drawing mode
      const primaryDrawActivation = button === 0 || isTouchPointer;
      if (canDraw && primaryDrawActivation && forcedDraw) {
        const drawMode = forcedDraw;
        if (!canStartDraw(drawMode)) return;
        const cell = getCellCoordinates(x, y);
        if (
          cell.x >= 0 &&
          cell.x < mapMetadata.width &&
          cell.y >= 0 &&
          cell.y < mapMetadata.height
        ) {
          drawTypeRef.current = drawMode;
          if (drawingArrowType !== drawMode) {
            onStartDrawArrow?.(drawMode);
          }
          isDrawingArrowRef.current = true;
          arrowPathRef.current = [{ x: cell.x, y: cell.y }];
          lastArrowPointRef.current = { x: cell.x, y: cell.y };
          onArrowPathUpdate?.([{ x: cell.x, y: cell.y }]);
        }
        return;
      }

    },
    [
      foundingNation,
      buildingStructure,
      drawingArrowType,
      uiMode,
      getCellCoordinates,
      mapMetadata,
      onArrowPathUpdate,
      onStartDrawArrow,
      onCancelArrow,
      onError,
      userId,
      clearLongPress,
      offset,
      beginPinchGesture,
    ]
  );

  const handlePointerMove = useCallback(
    (e) => {
      let x, y;
      if (e.data && e.data.global) {
        ({ x, y } = e.data.global);
      } else {
        const rect = e.target.getBoundingClientRect();
        x = e.nativeEvent.clientX - rect.left;
        y = e.nativeEvent.clientY - rect.top;
      }
      const originalEvent = e.data?.originalEvent || e.nativeEvent || {};
      const pointerId = originalEvent.pointerId ?? "mouse";
      const pointerType = originalEvent.pointerType || "mouse";
      const isTouchPointer = pointerType === "touch";
      if (isTouchPointer) {
        originalEvent.preventDefault?.();
      }

      if (isTouchPointer) {
        touchPointsRef.current.set(pointerId, { x, y });
        const lp = longPressRef.current;
        if (lp.armed && !lp.triggered && lp.pointerId === pointerId) {
          const moved = Math.hypot(x - lp.startX, y - lp.startY);
          if (moved > TOUCH_LONG_PRESS_MOVE_PX) {
            clearLongPress();
          }
        }
      }

      if (pinchRef.current.active) {
        const { ids, startDistance, startScale, lastMid } = pinchRef.current;
        const a = touchPointsRef.current.get(ids[0]);
        const b = touchPointsRef.current.get(ids[1]);
        if (a && b) {
          const mid = {
            x: (a.x + b.x) / 2,
            y: (a.y + b.y) / 2,
          };
          const distance = Math.max(1, Math.hypot(b.x - a.x, b.y - a.y));
          const targetScale =
            startScale * (distance / Math.max(1, startDistance));
          zoomAtPoint(mid.x, mid.y, targetScale);
          if (lastMid) {
            panBy(mid.x - lastMid.x, mid.y - lastMid.y);
          }
          pinchRef.current.lastMid = mid;
        }
        return;
      }

      if (panRef.current.active) {
        const dx = x - panRef.current.startX;
        const dy = y - panRef.current.startY;
        if (Math.abs(dx) > 2 || Math.abs(dy) > 2) {
          panRef.current.moved = true;
        }
        setOffsetClamped({
          x: panRef.current.startOffset.x + dx,
          y: panRef.current.startOffset.y + dy,
        });
        return;
      }
      // Arrow drawing - add points to path, clamped to max range
      if (isDrawingArrowRef.current && (drawTypeRef.current || drawingArrowType)) {
        const cell = getCellCoordinates(x, y);
        if (
          cell.x >= 0 &&
          cell.x < mapMetadata.width &&
          cell.y >= 0 &&
          cell.y < mapMetadata.height
        ) {
          const last = lastArrowPointRef.current;
          if (!last || cell.x !== last.x || cell.y !== last.y) {
            // Compute max arrow range from config + player population
            const cfg = configRef.current;
            const baseRange = cfg?.territorial?.arrowBaseRange ?? 15;
            const rangePerSqrtPop = cfg?.territorial?.arrowRangePerSqrtPop ?? 0.15;
            const maxRangeCfg = cfg?.territorial?.arrowMaxRange ?? 60;
            const gs = gameStateRef.current;
            const playerNation = gs?.gameState?.nations?.find(
              (n) => sameOwner(n.owner, userId)
            );
            const pop = playerNation?.population || 0;
            const maxRange = Math.min(maxRangeCfg, baseRange + Math.sqrt(pop) * rangePerSqrtPop);

            // Compute current path length
            const path = arrowPathRef.current;
            let pathLen = 0;
            for (let i = 1; i < path.length; i++) {
              pathLen += Math.hypot(path[i].x - path[i - 1].x, path[i].y - path[i - 1].y);
            }
            const segLen = Math.hypot(cell.x - last.x, cell.y - last.y);

            // Only add point if within max range
            if (pathLen + segLen <= maxRange) {
              arrowPathRef.current.push({ x: cell.x, y: cell.y });
              lastArrowPointRef.current = { x: cell.x, y: cell.y };
              onArrowPathUpdate?.([...arrowPathRef.current]);
            }
            // else: silently stop extending — arrow is at max range
          }
        }
        return;
      }
      const cell = getCellCoordinates(x, y);
      if (
        cell.x >= 0 &&
        cell.x < mapMetadata.width &&
        cell.y >= 0 &&
        cell.y < mapMetadata.height
      ) {
        pendingHoverRef.current = cell;
      } else {
        pendingHoverRef.current = null;
      }
      if (!hoverFrameRef.current) {
        hoverFrameRef.current = requestAnimationFrame(() => {
          hoverFrameRef.current = null;
          setHoveredCell(pendingHoverRef.current);
        });
      }
    },
    [
      getCellCoordinates,
      mapMetadata,
      drawingArrowType,
      onArrowPathUpdate,
      setOffsetClamped,
      panBy,
      zoomAtPoint,
      clearLongPress,
    ]
  );

  // Clean up arrow drawing state — shared by pointerup and pointerleave
  const finishArrowDrawing = useCallback(
    (submit) => {
      if (!isDrawingArrowRef.current) return;
      isDrawingArrowRef.current = false;
      const drawType = drawTypeRef.current || drawingArrowType;
      const path = [...arrowPathRef.current];
      arrowPathRef.current = [];
      lastArrowPointRef.current = null;
      drawTypeRef.current = null;

      if (submit) {
        // Check affordability before sending (mirrors server rules incl.
        // own-territory discount so we never reject an arrow the server allows)
        if (drawType === "attack") {
          const pNation = nations.find((n) => sameOwner(n.owner, userId));
          const cost = computeArrowCostInfo(
            path,
            config?.arrowCosts,
            pNation,
            ownershipMap,
            userId
          );
          if (!cost.affordable) {
            onError?.(
              `This arrow costs ${cost.foodCost} food and ${cost.goldCost} gold — you don't have enough.`
            );
            onCancelArrow?.();
            return;
          }
        }

        const simplifiedPath = simplifyArrowPath(path, 3);
        if (simplifiedPath.length >= 2) {
          onSendArrow?.(drawType, simplifiedPath);
        } else {
          onCancelArrow?.();
        }
      } else {
        onCancelArrow?.();
      }
    },
    [drawingArrowType, onSendArrow, onCancelArrow, onError, config, nations, userId, ownershipMap]
  );

  const handlePointerLeave = useCallback(() => {
    clearLongPress();
    touchPointsRef.current.clear();
    pinchRef.current = {
      active: false,
      ids: [],
      startDistance: 1,
      startScale: 1,
      lastMid: null,
    };
    // If the pointer leaves the canvas while drawing, cancel the arrow
    finishArrowDrawing(false);
    if (panRef.current.active) {
      panRef.current.active = false;
      panRef.current.moved = false;
      panRef.current.button = null;
      setIsPanning(false);
    }
  }, [finishArrowDrawing, clearLongPress]);

  const handlePointerUp = useCallback(
    (e) => {
      const originalEvent = e.data?.originalEvent || e.nativeEvent || {};
      const pointerId = originalEvent.pointerId ?? "mouse";
      const pointerType = originalEvent.pointerType || "mouse";
      const isTouchPointer = pointerType === "touch";
      if (isTouchPointer) {
        originalEvent.preventDefault?.();
      }
      if (isTouchPointer) {
        if (longPressRef.current.pointerId === pointerId) {
          clearLongPress();
        }
        touchPointsRef.current.delete(pointerId);
        if (pinchRef.current.active) {
          const [idA, idB] = pinchRef.current.ids;
          const keepPinch =
            touchPointsRef.current.has(idA) && touchPointsRef.current.has(idB);
          if (!keepPinch) {
            pinchRef.current = {
              active: false,
              ids: [],
              startDistance: 1,
              startScale: 1,
              lastMid: null,
            };
            setIsPanning(false);
          }
          return;
        }
      }
      if (panRef.current.active) {
        const wasMoved = panRef.current.moved;
        const panButton = panRef.current.button;
        panRef.current.active = false;
        panRef.current.moved = false;
        panRef.current.button = null;
        setIsPanning(false);
        if (panButton === 1 || panButton === 2 || wasMoved) {
          return;
        }
      }
      // Arrow drawing completed
      if (isDrawingArrowRef.current && (drawTypeRef.current || drawingArrowType)) {
        const hadArrowSegment = arrowPathRef.current.length >= 2;
        finishArrowDrawing(true);
        if (hadArrowSegment) {
          return;
        }
      }
      let x, y;
      if (e.data?.global) {
        x = e.data.global.x;
        y = e.data.global.y;
      } else {
        const rect = e.target.getBoundingClientRect();
        x = e.nativeEvent.clientX - rect.left;
        y = e.nativeEvent.clientY - rect.top;
      }
      const cell = getCellCoordinates(x, y);
      if (
        cell.x < 0 ||
        cell.x >= mapMetadata.width ||
        cell.y < 0 ||
        cell.y >= mapMetadata.height
      ) {
        return;
      }
      if (foundingNation) {
        onFoundNation?.(cell.x, cell.y);
        return;
      }
      if (buildingStructure) {
        const gridCellData = mapGridByRow[cell.y]?.[cell.x];
        const resourceStructureMapping = {
          farm: "food",
          "lumber mill": "wood",
          mine: ["stone", "iron", "gold"],
        };
        const required = resourceStructureMapping[buildingStructure];
        const cellResources = gridCellData ? gridCellData[5] : [];
        let resourceValid = Array.isArray(required)
          ? required.some((r) => cellResources.includes(r))
          : cellResources.includes(required);
        const userNation = gameState?.gameState?.nations?.find(
          (n) => sameOwner(n.owner, userId)
        );
        let territoryValid = false;
        if (userNation?.territory?.x && userNation?.territory?.y) {
          const tx = userNation.territory.x;
          const ty = userNation.territory.y;
          for (let ti = 0; ti < tx.length; ti++) {
            if (tx[ti] === cell.x && ty[ti] === cell.y) {
              territoryValid = true;
              break;
            }
          }
        }
        resourceValid =
          resourceValid ||
          ["town", "capital", "fort"].includes(buildingStructure);
        if (resourceValid && territoryValid) {
          onBuildCity?.(cell.x, cell.y, buildingStructure);
        } else if (!territoryValid) {
          onError?.("You can only build inside your own territory.");
        } else {
          onError?.(
            `A ${buildingStructure} must be placed on a matching resource tile.`
          );
        }
        onCancelBuild();
        return;
      }
      if (uiMode === "idle" && onInspectCell) {
        const gridCellData = mapGridByRow[cell.y]?.[cell.x];
        const info = gridCellData
          ? buildCellInfo(gridCellData, cell.x, cell.y)
          : null;
        if (info) onInspectCell(info);
      }
    },
    [
      getCellCoordinates,
      mapMetadata,
      foundingNation,
      onFoundNation,
      buildingStructure,
      onBuildCity,
      mapGrid,
      gameState,
      userId,
      onCancelBuild,
      drawingArrowType,
      finishArrowDrawing,
      onInspectCell,
      onError,
      buildCellInfo,
      clearLongPress,
    ]
  );

  // Prepare textures mapping for MapTiles
  const textures = useMemo(() => {
    const result = {};
    if (mappings && mappings.biomes) {
      Object.values(mappings.biomes).forEach((biome) => {
        const key = biome.toLowerCase();
        result[key] = `/biomes/${key}.png`;
      });
    }
    result["default"] = `/biomes/grassland.png`;
    return result;
  }, [mappings]);

  /* ----- Render Nation Overlays using NationOverlay ----- */
  const visibleBounds = useMemo(() => {
    if (!mapMetadata) return null;
    const visibleLeft = -offset.x / scale;
    const visibleTop = -offset.y / scale;
    const visibleRight = visibleLeft + viewportWidth / scale;
    const visibleBottom = visibleTop + viewportHeight / scale;
    const minX = Math.max(0, Math.floor(visibleLeft / cellSize) - 2);
    const maxX = Math.min(
      mapMetadata.width - 1,
      Math.ceil(visibleRight / cellSize) + 2
    );
    const minY = Math.max(0, Math.floor(visibleTop / cellSize) - 2);
    const maxY = Math.min(
      mapMetadata.height - 1,
      Math.ceil(visibleBottom / cellSize) + 2
    );
    return { minX, maxX, minY, maxY };
  }, [mapMetadata, offset, scale, viewportWidth, viewportHeight, cellSize]);

  const renderNationOverlays = useMemo(() => {
    const overlays = [];
    nations.forEach((nation, nationIndex) => {
      overlays.push(
        <NationOverlay
          key={`nation-${nation.owner}`}
          nation={nation}
          nationIndex={nationIndex}
          cellSize={cellSize}
          scale={scale}
          userId={userId}
          nationColors={nationColors}
          ownershipMap={ownershipMap}
          visibleBounds={visibleBounds}
        />
      );
    });
    return overlays;
  }, [
    nations,
    cellSize,
    scale,
    userId,
    nationColors,
    ownershipMap,
    visibleBounds,
  ]);

  // Compute the visible portion of the map for performance.
  const visibleMapGrid = useMemo(() => {
    if (!mapMetadata || mapGridByRow.length === 0 || !visibleBounds) return [];
    const visibleCells = [];
    for (let y = visibleBounds.minY; y <= visibleBounds.maxY; y++) {
      const row = mapGridByRow[y];
      if (!row) continue;
      for (let x = visibleBounds.minX; x <= visibleBounds.maxX; x++) {
        const cell = row[x];
        if (!cell) continue;
        visibleCells.push({ cell, x, y });
      }
    }
    return visibleCells;
  }, [mapGridByRow, mapMetadata, visibleBounds]);

  // Memoize resources rendering (only visible cells)
  const memoizedResources = useMemo(() => {
    if (scale < 0.4) return null;
    if (
      process.env.REACT_APP_DEBUG_RESOURCES === "true" &&
      visibleMapGrid.length &&
      mappings?.resources
    ) {
      const counts = {};
      visibleMapGrid.slice(0, 200).forEach(({ cell }) => {
        const resources = Array.isArray(cell[5]) ? cell[5] : [];
        resources.forEach((r) => {
          const name = mappings.resources[r] ?? r;
          counts[name] = (counts[name] || 0) + 1;
        });
      });
      console.log("[CLIENT RESOURCES] sample counts", counts);
    }
    return renderResources(visibleMapGrid, cellSize, scale);
  }, [visibleMapGrid, cellSize, scale]);
  const memoizedCaptureOverlays = useMemo(() => {
    if (scale < 0.45) return null;
    return renderResourceCaptureOverlays(
      visibleMapGrid,
      cellSize,
      ownershipMap,
      resourceNodeClaims,
      captureTicks
    );
  }, [visibleMapGrid, cellSize, scale, ownershipMap, resourceNodeClaims, captureTicks]);

  const captureEffectNodes = useMemo(() => {
    if (!captureEffects.length) return null;
    const now = captureEffectTime || performance.now();
    const invScale = 1 / Math.max(0.35, scale || 1);
    const baseFontSize = Math.max(16, Math.min(26, cellSize * 0.9));
    const riseDistance = 22 / Math.max(scale || 1, 0.35);
    return captureEffects.map((effect) => {
      const t = Math.min(
        1,
        Math.max(0, (now - effect.createdAt) / CAPTURE_EFFECT_DURATION_MS)
      );
      const fade =
        t < 0.2 ? t / 0.2 : Math.max(0, 1 - (t - 0.2) / 0.8);
      const pulse = t < 0.25 ? 0.6 + 0.4 * Math.sin((t / 0.25) * Math.PI) : 1;
      const alpha = Math.min(1, fade * pulse);
      const baseX = effect.x * cellSize + cellSize / 2;
      const baseY = effect.y * cellSize + cellSize / 2 - cellSize * 0.2;
      return (
        <Text
          key={effect.id}
          text={effect.text}
          x={baseX}
          y={baseY - riseDistance * t}
          anchor={0.5}
          zIndex={230}
          alpha={alpha}
          scale={{ x: invScale, y: invScale }}
          style={{
            fontFamily: "Barlow Semi Condensed, system-ui",
            fill: "#7CFF6B",
            fontSize: baseFontSize,
            fontWeight: "600",
            stroke: "#0b2a16",
            strokeThickness: 3,
          }}
        />
      );
    });
  }, [captureEffects, captureEffectTime, scale, cellSize]);
  // Note: memoizedTowers removed - towers are now rendered as structures in NationOverlay

  const visibleTileCount = useMemo(() => {
    if (!visibleBounds || !mapMetadata) return 0;
    return (
      (visibleBounds.maxX - visibleBounds.minX + 1) *
      (visibleBounds.maxY - visibleBounds.minY + 1)
    );
  }, [visibleBounds, mapMetadata]);

  const useFullMapTexture =
    !!fullMapImageUrl && (scale <= 0.65 || visibleTileCount > 60000);

  // Build mode preview for structures
  // Compute region info for build mode
  const hoveredRegionId = useMemo(() => {
    if (!regionData || !hoveredCell) return null;
    const { assignment, width } = regionData;
    const idx = hoveredCell.y * width + hoveredCell.x;
    if (idx < 0 || idx >= assignment.length) return null;
    return assignment[idx];
  }, [regionData, hoveredCell]);

  const regionBuildable = useMemo(() => {
    if (!regionData || hoveredRegionId === null || hoveredRegionId === 65535) return true;
    if (!buildingStructure) return true;
    const allNations = gameState?.gameState?.nations || [];
    const regionCfg = config?.regions;
    if (!regionCfg?.enabled) return true;

    const { assignment, width } = regionData;

    if (buildingStructure === "town") {
      let townCount = 0;
      for (const n of allNations) {
        for (const c of n.cities || []) {
          if ((c.type === "town" || c.type === "capital") &&
              c.y * width + c.x >= 0 && c.y * width + c.x < assignment.length &&
              assignment[c.y * width + c.x] === hoveredRegionId) {
            townCount++;
          }
        }
      }
      return townCount < (regionCfg.maxTownsPerRegion ?? 1);
    }

    if (buildingStructure === "tower") {
      const playerNation = allNations.find((n) => sameOwner(n.owner, userId));
      if (!playerNation) return true;
      let towerCount = 0;
      for (const c of playerNation.cities || []) {
        if (c.type === "tower" &&
            c.y * width + c.x >= 0 && c.y * width + c.x < assignment.length &&
            assignment[c.y * width + c.x] === hoveredRegionId) {
          towerCount++;
        }
      }
      return towerCount < (regionCfg.maxTowersPerRegion ?? 2);
    }

    return true;
  }, [regionData, hoveredRegionId, buildingStructure, gameState, config, userId]);

  let buildPreview = null;
  if (buildingStructure && hoveredCell) {
    const gridCellData = mapGridByRow[hoveredCell.y]?.[hoveredCell.x];
    const resourceStructureMapping = {
      farm: "food",
      "lumber mill": "wood",
      mine: ["stone", "iron", "gold"],
    };
    const required = resourceStructureMapping[buildingStructure];
    const cellResources = gridCellData ? gridCellData[5] : [];
    let resourceValid = Array.isArray(required)
      ? required.some((r) => cellResources.includes(r))
      : cellResources.includes(required);
    const userNation = gameState?.gameState?.nations?.find(
      (n) => sameOwner(n.owner, userId)
    );
    let territoryValid = false;
    if (userNation?.territory?.x && userNation?.territory?.y) {
      const tx = userNation.territory.x;
      const ty = userNation.territory.y;
      for (let ti = 0; ti < tx.length; ti++) {
        if (tx[ti] === hoveredCell.x && ty[ti] === hoveredCell.y) {
          territoryValid = true;
          break;
        }
      }
    }
    resourceValid =
      resourceValid || ["town", "capital", "fort", "tower"].includes(buildingStructure);
    const valid = resourceValid && territoryValid;
    const borderColor = valid ? 0x00ff00 : 0xff0000;
    const previewTexture =
      buildIconMap[buildingStructure] ||
      `/${buildingStructure.toLowerCase().replace(" ", "_")}.png`;
    buildPreview = (
      <BorderedSprite
        texture={previewTexture}
        x={hoveredCell.x * cellSize + cellSize / 2}
        y={hoveredCell.y * cellSize + cellSize / 2}
        width={cellSize * 2}
        height={cellSize * 2}
        borderColor={borderColor}
        isSelected={true}
        borderWidth={2}
        alpha={0.5}
        zIndex={300}
      />
    );
  }

  const sceneChildren = useMemo(() => {
    const children = [];
    if (useFullMapTexture && fullMapImageUrl && mapMetadata) {
      children.push(
        <Sprite
          key="full-map"
          image={fullMapImageUrl}
          x={0}
          y={0}
          width={mapMetadata.width * cellSize}
          height={mapMetadata.height * cellSize}
        />
      );
    } else {
      children.push(
        <MapTiles
          key="map-tiles"
          mapGrid={visibleMapGrid}
          cellSize={cellSize}
          mappings={mappings}
          textures={textures}
        />
      );
      if (memoizedResources) {
        if (Array.isArray(memoizedResources)) {
          children.push(...memoizedResources);
        } else {
          children.push(memoizedResources);
        }
      }
      if (memoizedCaptureOverlays) {
        if (Array.isArray(memoizedCaptureOverlays)) {
          children.push(...memoizedCaptureOverlays);
        } else {
          children.push(memoizedCaptureOverlays);
        }
      }
    }

    if (captureEffectNodes) {
      if (Array.isArray(captureEffectNodes)) {
        children.push(...captureEffectNodes);
      } else {
        children.push(captureEffectNodes);
      }
    }

    if (USE_OPTIMIZED_TERRITORY && mapMetadata) {
      children.push(
        <TerritoryLayer
          key="territory-layer"
          mapWidth={mapMetadata.width}
          mapHeight={mapMetadata.height}
          cellSize={cellSize}
          nations={nations}
          nationColors={nationColors}
          zIndex={100}
        />
      );
    } else if (renderNationOverlays?.length) {
      children.push(...renderNationOverlays);
    }

    // Troop density heatmap (after territory, before arrows)
    if (troopDensityMap && scale > 0.3) {
      children.push(
        <TroopDensityHeatmap
          key="troop-density-heatmap"
          densityMap={troopDensityMap}
          cellSize={cellSize}
          visibleBounds={visibleBounds}
        />
      );
    }

    // Border pressure visualization
    if (troopDensityMap && scale > 0.35 && playerNation) {
      children.push(
        <BorderPressureOverlay
          key="border-pressure"
          playerNation={playerNation}
          densityMap={troopDensityMap}
          cellSize={cellSize}
          visibleBounds={visibleBounds}
        />
      );
    }

    // Combat flash effects
    if (combatFlashes && combatFlashes.length > 0 && scale > 0.5) {
      children.push(
        <CombatFlashLayer
          key="combat-flashes"
          combatFlashes={combatFlashes}
          cellSize={cellSize}
          animTime={captureEffectTime}
          visibleBounds={visibleBounds}
        />
      );
    }

    // NOTE: Attack and defend arrows are rendered OUTSIDE this useMemo
    // (directly in the JSX below) so they always reflect the latest state
    // and don't get stuck from stale memoization.

    // Region overlay
    if (regionData) {
      children.push(
        <RegionOverlay
          key="region-overlay"
          regionData={regionData}
          cellSize={cellSize}
          visibleBounds={visibleBounds}
          scale={scale}
          buildMode={!!buildingStructure}
          hoveredRegionId={hoveredRegionId}
          regionBuildable={regionBuildable}
        />
      );
    }

    if (drawingArrowType && currentArrowPath && currentArrowPath.length > 0) {
      let arrowAffordable = true;
      let arrowCostText = null;

      if (config?.arrowCosts && drawingArrowType === "attack") {
        const pNation = nations.find((n) => sameOwner(n.owner, userId));
        const cost = computeArrowCostInfo(
          currentArrowPath,
          config.arrowCosts,
          pNation,
          ownershipMap,
          userId
        );
        arrowAffordable = cost.affordable;
        if (cost.foodCost > 0 || cost.goldCost > 0) {
          arrowCostText = `${cost.foodCost} food, ${cost.goldCost} gold`;
          if (!arrowAffordable) arrowCostText += " (insufficient)";
        } else {
          arrowCostText = "Free";
        }
      }

      const arrowColor = arrowAffordable
        ? (drawingArrowType === "attack" ? 0xff4444 : 0x4444ff)
        : 0x888888;

      const node = renderArrowPath(
        currentArrowPath,
        cellSize,
        drawingArrowType,
        false,
        "drawing-arrow",
        null,
        scale,
        arrowColor
      );
      if (node) children.push(node);

      // Arrow cost label near the end of the path
      if (arrowCostText && currentArrowPath.length >= 2) {
        const lastPt = currentArrowPath[currentArrowPath.length - 1];
        const invScale = 1 / Math.max(0.35, scale || 1);
        children.push(
          <Container
            key="arrow-cost-label"
            x={lastPt.x * cellSize + cellSize}
            y={lastPt.y * cellSize - cellSize}
            zIndex={250}
            scale={{ x: invScale, y: invScale }}
          >
            <Text
              text={arrowCostText}
              style={{
                fontSize: 12,
                fill: arrowAffordable ? "#44cc44" : "#ff4444",
                fontWeight: "bold",
                stroke: "#000000",
                strokeThickness: 2,
              }}
            />
          </Container>
        );
      }
    }

    if (buildPreview) children.push(buildPreview);

    return children;
  }, [
    useFullMapTexture,
    fullMapImageUrl,
    mapMetadata,
    cellSize,
    visibleMapGrid,
    mappings,
    textures,
    memoizedResources,
    memoizedCaptureOverlays,
    captureEffectNodes,
    renderNationOverlays,
    troopDensityMap,
    playerNation,
    combatFlashes,
    captureEffectTime,
    visibleBounds,
    drawingArrowType,
    currentArrowPath,
    buildPreview,
    nations,
    nationColors,
    regionData,
    hoveredRegionId,
    regionBuildable,
    buildingStructure,
    config,
    userId,
    scale,
  ]);

  return (
    <Stage
      interactive={true}
      width={viewportWidth}
      height={viewportHeight}
      options={{
        backgroundColor: 0x245c82,
        antialias: scale > 0.6,
        resolution: scale < 0.45 ? 1 : window.devicePixelRatio || 1,
        autoDensity: true,
      }}
      onContextMenu={(e) => e.preventDefault()}
      onWheel={handleWheel}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerLeave}
      style={{
        width: "100%",
        height: "100%",
        touchAction: isDiscord || isMobile ? "none" : "auto",
        userSelect: "none",
        WebkitUserSelect: "none",
        WebkitTouchCallout: "none",
        cursor:
          buildingStructure ||
          foundingNation ||
          uiMode === "drawAttack" ||
          uiMode === "drawDefend" ||
          drawingArrowType
            ? "crosshair"
            : isPanning
            ? "grabbing"
            : uiMode === "pan"
            ? "grab"
            : "grab",
      }}
    >
      <Container
        x={offset.x}
        y={offset.y}
        scale={scale}
        sortableChildren={true}
      >
        {sceneChildren}
        {/* Attack arrows rendered outside useMemo so they always reflect latest state */}
        {smoothedAttackArrows?.map((arrow, i) =>
          arrow?.path
            ? renderArrowV2(arrow, cellSize, `active-attack-${arrow.id || i}`, scale)
            : null
        )}
        {/* Animated troop flow dots along arrow paths */}
        {scale > 0.5 && smoothedAttackArrows?.map((arrow, i) =>
          arrow?.path ? (
            <ArrowTroopFlow
              key={`troop-flow-${arrow.id || i}`}
              arrow={arrow}
              cellSize={cellSize}
              scale={scale}
              visibleBounds={visibleBounds}
            />
          ) : null
        )}
        {activeDefendArrow?.path
          ? renderArrowPath(
              activeDefendArrow.path,
              cellSize,
              "defend",
              true,
              "active-defend-arrow",
              activeDefendArrow.remainingPower,
              scale
            )
          : null}
      </Container>
    </Stage>
  );
};

export default GameCanvas;
